import { useTheme } from "@/context/ThemeContext";

// Map of full-text → simplified equivalent. When SIMPLIFIED reading style is
// active, `simplify()` swaps in the shorter version. Unknown strings pass
// through unchanged so partial coverage is safe.
export const SIMPLIFIED_COPY: Record<string, string> = {
  "Intricate seed bead embroidery and fabric appliqué confirmed": "Hand beading confirmed",
  "Construction details fully consistent with stated era": "Construction matches the era",
  "Label present and fully legible": "Label clear and confirmed",
  "Material characteristics fully consistent": "Material confirmed",
  "Insufficient photos to assess": "Need more photos",
  "Strong evidence across all signals": "All checks passed",
  "Good evidence — one or more signals limited by photo quality": "Most checks passed",
  "Partial evidence — consider resubmitting with additional photos": "Some checks incomplete",
  // ─── Generic error replacements ───
  // Format: short sentence, says what failed + what to do next. Avoid jargon.
  "Something went wrong": "This didn't save. Please try again.",
  "Invalid input": "Please check your entry and try again.",
  "Upload failed": "Your photo didn't upload. Try a smaller file or check your connection.",
  "Verification failed": "We couldn't verify this item. Try resubmitting with clearer photos.",
  "Submission failed": "We couldn't submit this. Please try again.",
  "Retry failed": "Couldn't try again. Please wait a moment and retry.",
  "Approve failed": "Couldn't approve this. Please try again.",
  "Bulk update failed": "Couldn't update those items. Please try again.",
  "Backfill failed": "Couldn't refresh that list. Please try again.",
  "Distill failed": "Couldn't summarize this. Please try again.",
  "Could not reject": "Couldn't dismiss that item. Please try again.",
  "Could not post combined": "Couldn't combine those items. Please try again.",
  "Couldn't post your note": "Your note didn't send. Please try again.",
  "Couldn't delete that note": "Couldn't delete your note. Please try again.",
  "Couldn't update like": "Couldn't update that like. Please try again.",
  "Couldn't update pin": "Couldn't pin that note. Please try again.",
  "Couldn't undo reaction": "Couldn't undo that reaction. Please try again.",
  "Couldn't block that user": "Couldn't block this person. Please try again.",
  "Couldn't unblock that user": "Couldn't unblock this person. Please try again.",
  "Couldn't load feedback": "Couldn't load feedback. Please refresh.",
  "Couldn't load suggestions": "Couldn't load suggestions. Please refresh.",
  "Couldn't load lessons": "Couldn't load lessons. Please refresh.",
  "Couldn't mark reviewed": "Couldn't mark this reviewed. Please try again.",
  "Couldn't update": "Couldn't save your change. Please try again.",
  "Couldn't save your hunt": "Couldn't save this hunt. Please try again.",
  "Couldn't copy the link": "Couldn't copy the link. Please try again.",
  "Couldn't mark sold": "Couldn't mark this sold. Please try again.",
  "Couldn't export your sales": "Couldn't export your sales. Please try again.",
  "Couldn't save shops": "Couldn't save your shops. Please try again.",
  "Couldn't save preference": "Couldn't save that setting. Please try again.",
  "Couldn't send. Try again.": "This didn't send. Please try again.",
  "Couldn't open conversation": "Couldn't open this chat. Please try again.",
  "Couldn't add that grail. Try again.": "Couldn't save this grail. Please try again.",
  "Couldn't read this one": "Couldn't read this photo. Try a clearer image.",
  "Couldn't post": "We couldn't post this. Please try again.",
  "Not enough scans for this batch": "You don't have enough scans left.",
  "Not enough scans to retry": "You don't have enough scans left to retry.",
};

export function simplifyText(input: string, simplified: boolean): string {
  if (!simplified) return input;
  return SIMPLIFIED_COPY[input] ?? input;
}

// ─── Module-level mirror for non-React contexts (toasts, async callbacks) ──
// Components can't easily call hooks from a setTimeout / promise rejection
// path, so we keep a synchronous reflection of the active reading style here.
// `<ReadingStyleSync />` (rendered inside ThemeProvider) keeps it fresh.
let _simplifiedActive = false;
export function setSimplifiedActive(active: boolean) {
  _simplifiedActive = active;
}
export function simplifyMaybe(input: string | undefined | null): string {
  if (input == null) return "";
  return simplifyText(String(input), _simplifiedActive);
}

/** Hook returning the current reading style + a simplifier helper. */
export function useReadingStyle() {
  const { prefs } = useTheme();
  const simplified = prefs.readingStyle === "simplified";
  return {
    readingStyle: prefs.readingStyle,
    simplified,
    simplify: (text: string) => simplifyText(text, simplified),
  };
}